// Centralized admin API helpers. Keep all fetches here so tabs stay declarative.
import { authFetch } from '../../auth/token';

const API = process.env.REACT_APP_API_BASE_URL;

// authFetch has already refreshed and retried by the time a 401 reaches here.
const handleError = async (res, includeBody = true) => {
    let body = '';
    if (includeBody) {
        try { body = await res.text(); } catch { /* ignore */ }
    }
    let serverMessage = '';
    try { serverMessage = JSON.parse(body).message; } catch { /* not JSON */ }
    throw new Error(serverMessage || `${res.status} ${res.statusText}${body ? ` — ${body}` : ''}`);
};

async function asJson(res) {
    if (!res.ok) {
        return handleError(res);
    }
    // 204 No Content (e.g. POST /promos/map) has no body to parse.
    const body = await res.text();
    return body ? JSON.parse(body) : null;
}

const json = (method, url, body) => authFetch(url, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body !== undefined ? JSON.stringify(body) : undefined,
}).then(asJson);

const text = (method, url, body) => authFetch(url, {
    method,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
}).then(async (res) => {
    if (!res.ok) {
        return handleError(res, false);
    }
    return res.text();
});

// ----- Listings -----
export const Listings = {
    list: ({ page = 1, count = 50, sellerEmail, certNumber, listingId } = {}) => {
        const p = new URLSearchParams({ page, count });
        if (sellerEmail) p.set('sellerEmail', sellerEmail);
        if (certNumber) p.set('certNumber', certNumber);
        if (listingId) p.set('listingId', listingId);
        return json('GET', `${API}/listings?${p}`);
    },
    create: (payload) => json('POST', `${API}/listings`, payload),
    // PATCH only writes the fields it's given (nulls are skipped server-side).
    update: ({ listing_id, price, wholesale_price, auto_price }) =>
        json('PATCH', `${API}/listings`, { listing_id, price, wholesale_price, auto_price }),
    deactivate: (certNumber) =>
        text('DELETE', `${API}/listings/deactivate?certNumber=${encodeURIComponent(certNumber)}`),
};

// ----- Certs -----
export const Certs = {
    list: ({ page = 1, count = 50, certNumber, cardName, specId, hasImages = true } = {}) => {
        const p = new URLSearchParams({ page, count });
        if (hasImages) p.set('hasImages', 'true');
        if (certNumber) p.set('certNumber', certNumber);
        // Partial card-name search (backend must implement the filter on /certs).
        if (cardName) p.set('cardName', cardName);
        if (specId) p.set('specId', specId);
        return json('GET', `${API}/certs?${p}`);
    },
    // PATCH only writes the fields it's given. card_id and price_charting_id are
    // applied to every cert with the same PSA spec; returns the updated cert.
    patch: (certNumber, fields) =>
        json('PATCH', `${API}/certs`, { cert_number: certNumber, ...fields }),
    deactivate: (certNumber) =>
        text('POST', `${API}/certs/deactivate?certNumber=${encodeURIComponent(certNumber)}`),
};

// ----- Pricing -----
export const Pricing = {
    // Refresh PriceCharting pricing for every active cert. Long-running: the API
    // walks all active certs, so the request can take a while to come back.
    refreshAll: () => json('POST', `${API}/pricing/pricecharting`),
};

// ----- Cards -----
export const Cards = {
    list: ({ page = 1, count = 50, supertype, name, number } = {}) => {
        const p = new URLSearchParams({ page, count });
        if (supertype) p.set('supertype', supertype);
        if (name) p.set('name', name);
        if (number) p.set('number', number);
        return json('GET', `${API}/cards?${p}`);
    },
};

// ----- Orders -----
export const Orders = {
    list: ({ page = 1, count = 50, orderNumber, buyerEmail, sellerEmail, certNumber } = {}) => {
        const p = new URLSearchParams({ page, count });
        if (orderNumber) p.set('orderNumber', orderNumber);
        if (buyerEmail) p.set('buyerEmail', buyerEmail);
        if (sellerEmail) p.set('sellerEmail', sellerEmail);
        if (certNumber) p.set('certNumber', certNumber);
        return json('GET', `${API}/orders?${p}`);
    },
};

// ----- Users -----
export const Users = {
    // Every account with roles plus active-listing and order counts (Admin only).
    list: () => json('GET', `${API}/users`),
};

// ----- Auth (roles + sessions) -----
// The API refuses to grant Admin, and the Users tab only ever toggles Broker.
export const Auth = {
    roles: (email) => json('GET', `${API}/auth/getuserroles/${encodeURIComponent(email)}`),
    assignRole: (email, roleName) => text('POST', `${API}/auth/assignrole`, { email, role_name: roleName }),
    removeRole: (email, roleName) => text('POST', `${API}/auth/removerole`, { email, role_name: roleName }),
    // Ends every session the user has, right away.
    revoke: (email) => text('POST', `${API}/auth/revoke`, { email }),
};

// ----- Sets -----
export const Sets = {
    list: ({ page = 1 } = {}) => json('GET', `${API}/sets?page=${page}`),
};

// ----- Promos -----
export const Promos = {
    list: () => json('GET', `${API}/promos`),
    create: (payload) => json('POST', `${API}/promos`, payload),
    map: (certNumber, promoDbId) =>
        json('POST', `${API}/promos/map`, { cert_number: certNumber, promo_id: promoDbId }),
};
